// Right rail "Recent feedback" tab body (docs/06-ux-rules.md "Right rail" /
// "Recent feedback panel" / "Recent feedback — overflow"; veda:projects/
// unified-feedback-inbox/parts/p08-right-rail.md D4 as superseded by R3's
// look-back; VISUAL-SPEC.md §4 "Recent feedback tab"). Never rendered in
// look-back mode (RightRail omits the tab entirely — FR-63).
import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import type { FeedbackInboxRecentActivity } from 'api/feedbackInbox.types'
import FeedbackRatingIcon from 'components/FeedbackRatingIcon'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useMemo, useState } from 'react'
import { useStyletron } from 'styletron-react'
import { formatNumericDate } from 'utilities/dateFormatters'
import { RECENT_FEEDBACK_PREVIEW_COUNT } from '../constants'
import { textLinkButtonOverrides } from '../textLinkButtonOverrides'
import RailSectionHeading from './RailSectionHeading'

const t = buildTranslate('feedback_inbox.rail.recent_feedback')

// 2px belize ring for keyboard focus (WCAG 2.4.7), same value as
// `InboxFilterBar/index.tsx`'s own `FOCUS_RING_SHADOW` — re-declared here
// because that file's copy isn't exported for reuse.
const FOCUS_RING_SHADOW = `0 0 0 2px ${palette.belizeHole}`

// baseui drives its own style fn (and focus ring) off `$isFocusVisible`, not
// a `:focus-visible` CSS pseudo-class — see InboxFilterBar/index.tsx's own
// comment on `BaseButtonStyleProps`/`segmentButtonOverrides`.
type BaseButtonStyleProps = { $isFocusVisible: boolean }

// Thumb tint per rating — the same `cayenne05`/`nephritis10` pair as the
// list row's avatar. Intentional: v83 gives `.ib-r-thumb` and `.ib-ava` the
// one `#fdecec` tint (≈ cayenne05).
function thumbBackground(rating: FeedbackInboxRecentActivity['rating']) {
  return rating === 'up' ? palette.nephritis10 : palette.cayenne05
}

export interface RecentFeedbackPanelProps {
  // Resets the inline expansion whenever the viewed item changes (docs/06-
  // ux-rules.md "Recent feedback — overflow": "collapses on item change").
  itemId: string | null
  activities: FeedbackInboxRecentActivity[]
  onOpenLookback: (id: string) => void
}

const RecentFeedbackPanel = ({
  itemId,
  activities,
  onOpenLookback,
}: RecentFeedbackPanelProps) => {
  const [css] = useStyletron()
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    setExpanded(false)
  }, [itemId])

  // Defensive: the currently-viewed item should never appear in its own
  // "recent feedback from this guest" list, but filter it out rather than
  // trust every caller/fixture to have excluded it upstream.
  const otherActivities = useMemo(
    () => activities.filter(activity => activity.id !== itemId),
    [activities, itemId]
  )

  if (otherActivities.length === 0) {
    return (
      <Text.BodyText4 tag="p" color="grey70">
        {t('empty')}
      </Text.BodyText4>
    )
  }

  const visible = expanded
    ? otherActivities
    : otherActivities.slice(0, RECENT_FEEDBACK_PREVIEW_COUNT)
  const remaining = otherActivities.length - visible.length

  const rowClass = css({
    display: 'flex',
    width: '100%',
    alignItems: 'flex-start',
    gap: '10px',
  })

  const thumbClass = (rating: FeedbackInboxRecentActivity['rating']) =>
    css({
      flexShrink: 0,
      width: '20px',
      height: '20px',
      borderRadius: '5px',
      backgroundColor: thumbBackground(rating),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    })

  // See InboxFilterBar/index.tsx's header comment: an
  // `overrides.BaseButton.style` function deep-merges with baseui's own
  // `kind="minimal"` style generator (rather than replacing it outright) —
  // any state baseui's generator drives itself (`:hover`, `:disabled`, the
  // `$isFocusVisible` focus ring) has to be re-declared here explicitly or
  // it silently reverts to baseui's default treatment.
  const rowOverrides = {
    BaseButton: {
      style: ({ $isFocusVisible }: BaseButtonStyleProps) => ({
        display: 'block',
        width: '100%',
        padding: '8px 10px',
        margin: '0 -10px',
        borderRadius: '8px',
        textAlign: 'left' as const,
        backgroundColor: 'transparent',
        border: 'none',
        boxShadow: $isFocusVisible ? FOCUS_RING_SHADOW : 'none',
        ':hover': {
          backgroundColor: palette.grey05,
        },
        ':disabled': {
          backgroundColor: 'transparent',
        },
      }),
    },
  }

  const resolvedByLabel = (
    activity: FeedbackInboxRecentActivity
  ): string | null => {
    if (!activity.resolvedBy) return null
    const name =
      activity.resolvedBy.kind === 'ai'
        ? t('recovery_ai_name')
        : activity.resolvedBy.name
    return name ? t('resolved_by', { name }) : null
  }

  return (
    <div>
      {/* In-body heading is intentional here — VISUAL-SPEC.md §4. Same
          heading as the Customer and Order info tabs. */}
      <RailSectionHeading title={t('title')} />
      {/* The whole row is a link (docs/06-ux-rules.md "Recent feedback
          panel": "Whole row is a link") — a full-width minimal Button
          wrapping the row content, per the repo's "uikit Button over native
          button/span+onClick" convention. */}
      {visible.map(activity => {
        const resolvedLabel = resolvedByLabel(activity)
        const comment =
          activity.comment ||
          (activity.rating === 'up' ? t('no_comment') : null)
        return (
          <Button
            key={activity.id}
            kind="minimal"
            onClick={() => onOpenLookback(activity.id)}
            overrides={rowOverrides}
          >
            <div className={rowClass}>
              <div className={thumbClass(activity.rating)}>
                <FeedbackRatingIcon rating={activity.rating} size={12} />
              </div>
              <div className={css({ flex: 1, minWidth: 0 })}>
                <Text.BodyText4 bold tag="div" color="grey90">
                  {formatNumericDate(activity.date)}
                </Text.BodyText4>
                {/* The guest UI doesn't collect a comment on thumbs-up, so a
                    comment-less 👍 row gets a placeholder instead of a bare
                    date. A comment, if one exists, still wins. A 👎 row's `comment`
                    can be null while text exists (no PII consent, or a
                    RecoveryAI chat row), so it gets no placeholder. */}
                {comment && (
                  <Text.BodyText5
                    tag="div"
                    color="grey70"
                    className={css({
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    })}
                  >
                    {comment}
                  </Text.BodyText5>
                )}
                {resolvedLabel && (
                  <Text.BodyText5 tag="div" color="grey70">
                    {resolvedLabel}
                  </Text.BodyText5>
                )}
              </div>
            </div>
          </Button>
        )
      })}
      {remaining > 0 && (
        <Button
          kind="minimal"
          aria-expanded={expanded}
          onClick={() => setExpanded(true)}
          overrides={textLinkButtonOverrides({ marginTop: '8px' })}
        >
          {t('view_more', { count: remaining })}
        </Button>
      )}
    </div>
  )
}

export default RecentFeedbackPanel
